import React, { useState, useCallback } from 'react';
import EDPaper from '../../../BaseComponents/Paper/EDPaper';
import { EDTextEdit } from '../../../BaseComponents/TextEdit/EDTextEdit';
import EDButton from '../../../BaseComponents/Buttons/Button/EDButton';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Logger, { namedError } from '../../../Utils/Logger';
import ChipRadioButtons from '../../../Components/ChipRadioButtons/ChipRadioButtons';
import { useIsTouchDevice } from '../../../Components/BreakpointContext/BreakpointContextUtils';
import { RutinSettingsContext } from '../../Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { ekonomiskAnalysUrvalFieldsPredicates } from '../../Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Utils/ekonomiskAnalysFieldPredicates';
import { Leveransstatus } from '../../../Types/RutinInfoType';

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      container: {
        flexDirection: 'column',
        width: '100%',
        padding: 16,
      },
    } as const)
);

enum VarningsTyp {
  critical,
  error,
  warn,
  info,
  debug,
}

const LoggerDemo: React.FC<React.PropsWithChildren<{}>> = props => {
  const { classes } = useStyles();
  const [rollbarMessage, setRollbarMessage] = useState('');
  const [varningsTyp, setVarningsTyp] = useState(VarningsTyp.error);
  const updateVarningsTyp = useCallback(
    (value: VarningsTyp) => setVarningsTyp(value),
    [setVarningsTyp]
  );
  const createError = useCallback(() => {
    switch (varningsTyp) {
      case VarningsTyp.debug:
        Logger.debug(rollbarMessage);
        break;
      case VarningsTyp.info:
        Logger.info(rollbarMessage);
        break;
      case VarningsTyp.warn:
        Logger.warn(namedError('LoggerDemo-Demo', rollbarMessage));
        break;
      case VarningsTyp.error:
        Logger.error(rollbarMessage);
        break;
      case VarningsTyp.critical:
        Logger.critical(rollbarMessage);
        break;
    }
  }, [rollbarMessage, varningsTyp]);
  return (
    <EDPaper className={classes.container}>
      <EDTextEdit
        label="Logmeddelande"
        value={rollbarMessage}
        onChange={setRollbarMessage}
        staticLabel
        multiline
      />
      <ChipRadioButtons
        label="Varningstyp"
        value={varningsTyp}
        setValue={updateVarningsTyp}
        choices={[
          {
            value: VarningsTyp.debug,
            label: 'Debug',
            icon: 'developer_board',
          },
          { value: VarningsTyp.info, label: 'Info', icon: 'info' },
          { value: VarningsTyp.warn, label: 'Varning', icon: 'warning' },
          { value: VarningsTyp.error, label: 'Error', icon: 'error' },
          { value: VarningsTyp.critical, label: 'Kritiskt', icon: 'build' },
        ]}
      />
      <EDButton
        label="Logga meddelande"
        disabled={rollbarMessage === ''}
        disabledReason="Inget error angivet"
        onClick={createError}
      />
    </EDPaper>
  );
};

const EnhancedLoggerDemo = () => {
  const isTouchDevice = useIsTouchDevice();
  return (
    <RutinSettingsContext.Provider
      value={{
        settings: {},
        setActiveRoutine: () => {},
        setVisningsTyp: () => () => {},
        setDetaljurval: () => () => {},
        activeRoutine: {
          routine: {
            benamning: 'LoggerDemo',
            rutinID: 999999999999999,
            rutinKod: '999999999999999',
            rutinURL: 'loggerDemo',
            readonly: false,
            visningsTyper: {
              test: isTouchDevice ? 'lista' : 'tabell',
            },
            detaljurval: {},
            leveransstatus: Leveransstatus.Active,
          },
          predicates: ekonomiskAnalysUrvalFieldsPredicates,
        },
        rutinStartErrorDialogOpen: false,
        setRutinStartErrorDialogOpen: () => {},
        hasRutinStartError: false,
        setHasRutinStartError: () => {},
        setCurrentBookingGroup: () => {},
      }}
    >
      <LoggerDemo />
    </RutinSettingsContext.Provider>
  );
};

export default EnhancedLoggerDemo;
